import { useFormikContext } from 'formik';
import getEnumKeyByValue from 'helpers/getEnumKeyByValue';
import { Maths } from 'helpers/maths';
import { getInitials } from 'helpers/textHelpers';
import useProduct from 'hooks/useProduct';
import useUomConvert from 'hooks/useUomConvert';
import moment from 'moment';
import React, { useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ProductQuantityType } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';
import { OperationType } from 'screens/Applications&WorkOrders/RouteAdmin';
import {
  DEFAULT_OPERATION_DURATION_HOURS,
  DEFAULT_OPERATION_DURATION_MINUTES,
  DEFAULT_OPERATION_START_HOUR
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/defaultDuration';
import {
  FormValues,
  Product,
  RecordSourceType
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import generateDefaultApplicationId from 'screens/Applications&WorkOrders/StepsLayout/Helpers/generateDefaultApplicationId';
import { enrichProductsWithListPrice } from 'screens/Applications&WorkOrders/Components/helpers/enrichProductsWithListPrice';
import useInvoiceData from 'screens/Invoices/Invoice/hooks/useInvoiceData';
import { useAuth } from 'utilities/base-auth';

type SeasonNameAndIdVO = {
  seasonId?: string;
  name?: string;
  [key: string]: any;
};

type InvoiceProductVO = {
  id: string;
  productId: string;
  productName?: string;
  trackingId: string;
  totalQuantityValue?: number;
  totalQuantityUnit?: string;
  totalCostValue?: number;
  totalCostCurrency?: string;
  productDensity?: number;
  manufacturerName?: string;
  registrationNumber?: string;
  productStandardPackageUnit?: string;
  custom?: boolean;
  totalQuantityValueInStandardPackageUnit?: number;
  [key: string]: any;
};

type InvoiceDocumentDescriptorVO = {
  [key: string]: any;
};

export type InvoiceVO = {
  id: string;
  invoiceId?: string;
  invoiceDate: string;
  invoiceDateEpoch: number;
  invoiceNumber: string;
  vendor?: string;
  vendorName?: string;
  notes?: string;
  cropSeasons?: SeasonNameAndIdVO[];
  invoiceDueDate: string;
  invoiceDueDateEpoch: number;
  flagIds?: string[];
  invoiceProducts: InvoiceProductVO[];
  documentDescriptors?: InvoiceDocumentDescriptorVO[];
  organizationId?: string;
};

const useConversionInvoiceToOperation = () => {
  const location = useLocation<{
    invoice: InvoiceVO;
    targetOperation: OperationType;
  }>();
  const { invoice: invoiceState, targetOperation } = location.state;
  const {
    invoice: invoiceData,
    loading: isInvoiceLoading,
    fetchInvoice
  } = useInvoiceData(
    invoiceState.invoiceProducts ? undefined : invoiceState.invoiceId
  );
  const invoice: InvoiceVO = React.useMemo(
    () =>
      invoiceData ?? {
        ...invoiceState,
        id: invoiceState.invoiceId!
      },
    [invoiceData, invoiceState]
  );

  const { setValues } = useFormikContext<FormValues>();

  const { user } = useAuth();
  const { fetchProductData, getProduct } = useProduct();
  const convertFn = useUomConvert();

  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {
    if (!invoiceState.invoiceProducts) {
      fetchInvoice();
    }
  }, [invoiceState, fetchInvoice]);

  const calculatePriceValue = (invoiceProduct: InvoiceProductVO) => {
    const totalCost = invoiceProduct.totalCostValue || 0;
    const totalQuantity = invoiceProduct.totalQuantityValue || 0;
    if (totalQuantity === 0) {
      return 0;
    }
    return Maths.divideSafeRound(totalCost, totalQuantity);
  };

  const mapInvoiceProductToOperation = useCallback(
    (
      fetchedProducts: (InvoiceProductVO | null)[],
      invoiceProduct: InvoiceProductVO,
      index: number
    ): Product => {
      // Index-based lookup is more reliable than trackingId (which may be missing or
      // duplicated), and the arrays are always parallel (same order, same length).
      const fetchedProduct = fetchedProducts[index] ?? invoiceProduct;

      const unit =
        invoiceProduct.totalQuantityUnit || fetchedProduct.stdpackageunit;
      return {
        id: invoiceProduct.productId,
        productApplicationId: invoiceProduct.trackingId || crypto.randomUUID(),
        manufacturer: invoiceProduct.manufacturerName,
        // Invoice API may omit productType; fall back to the master product's type so
        // ProductTypeDisplay always receives a defined value and doesn't throw.
        productType: fetchedProduct.productType || '',
        quantityMode: getEnumKeyByValue(
          ProductQuantityType,
          ProductQuantityType.totalProduct
        ),
        totalProduct: invoiceProduct.totalQuantityValue || 0,
        totalProductUnit: unit,
        name: invoiceProduct.productName || fetchedProduct.name,
        ratePerAcre: 0,
        ratePerAreaUnit:
          invoiceProduct.totalQuantityUnit || fetchedProduct.stdpackageunit,
        ratePerTank: 0,
        ratePerTankUnit: unit,
        priceValue: calculatePriceValue(invoiceProduct),
        priceUnit:
          invoiceProduct.totalQuantityUnit ||
          invoiceProduct.productStandardPackageUnit,
        isInUse: true,
        totalCost: invoiceProduct.totalCostValue || 0,
        totalAppliedAcreage: 0,
        coveragePercent: 100,
        custom: invoiceProduct.custom || false,
        density: invoiceProduct.productDensity || fetchedProduct.density || 1,
        // Fall back to the invoice's productStandardPackageUnit when the master
        // product fetch fails so unit dropdowns are still populated.
        stdpackageunit:
          fetchedProduct.stdpackageunit ||
          invoiceProduct.productStandardPackageUnit,
        stdunit: fetchedProduct.stdunit,
        isCarrier: false,
        isHidden: false
      };
    },
    []
  );
  const convertInvoiceToOperationFormikData = useCallback(async (): Promise<
    void
  > => {
    setIsLoading(true);
    if (isInvoiceLoading) {
      return;
    }

    try {
      const startDateTime = moment(invoice.invoiceDate).set({
        hour: DEFAULT_OPERATION_START_HOUR,
        minute: 0,
        second: 0,
        millisecond: 0
      });
      const endDateTime = startDateTime
        .clone()
        .add(DEFAULT_OPERATION_DURATION_HOURS, 'hours');
      const durationHours = DEFAULT_OPERATION_DURATION_HOURS;
      const durationMinutes = DEFAULT_OPERATION_DURATION_MINUTES;

      const initials =
        user?.name && getInitials(user.name) ? getInitials(user.name) : '';

      const fetchedProducts: InvoiceProductVO[] = await Promise.all(
        invoice.invoiceProducts?.map(async invoiceProduct => {
          if (!invoiceProduct.productId) {
            return null;
          }
          const result = await fetchProductData(invoiceProduct);
          return result?.data ?? null;
        }) || []
      );

      const mappedProducts =
        invoice?.invoiceProducts?.map((invoiceProduct, index) =>
          mapInvoiceProductToOperation(fetchedProducts, invoiceProduct, index)
        ) || [];

      // When an invoice product has no entered price ($0), fall back to the
      // product's saved list price so the cost populates at creation time.
      const enrichedProducts = await enrichProductsWithListPrice(
        mappedProducts,
        getProduct,
        convertFn
      );

      setValues((previousValues: FormValues) => {
        return {
          ...previousValues,
          startDateTime,
          ...(targetOperation === OperationType.APPLICATION
            ? { endDateTime, durationHours, durationMinutes }
            : { weather: undefined }),
          applicationId: generateDefaultApplicationId(initials),
          authorizationDate: startDateTime,
          notes: invoice.notes || previousValues.notes,
          tagIds: invoice.flagIds || previousValues.tagIds,
          products: enrichedProducts,

          recordSource: {
            recordSourceType: RecordSourceType.INVOICE,
            recordSourceId: invoice.id,
            recordSourceName: invoice.invoiceNumber
          }
        };
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    targetOperation,
    isInvoiceLoading,
    invoice,
    setValues,
    user,
    fetchProductData,
    getProduct,
    convertFn,
    mapInvoiceProductToOperation
  ]);
  return {
    convertInvoiceToOperationFormikData,
    isLoading,
    operationType: targetOperation
  };
};
export default useConversionInvoiceToOperation;
